import AsyncControl from '@/Controls/AsyncControl';
import ComponentManager from '@/Management/ComponentManager';
import TabsStripTab2Type from '@/Controls/TabStripTab2';
import TabStripTab2ViewModel from '@/ViewModels/Controls/TabStripTab2ViewModel';
import TabStrip2ViewModel from '@/ViewModels/Controls/TabStrip2ViewModel';
import {DisposeBehavior} from '#/DefaultViewExtensionInterfaces';

export default class TabStrip2 extends AsyncControl {
    private TabStripTab2 = ComponentManager.getControl<typeof TabsStripTab2Type>('TabStripTab2');
    protected jQueryControl: JQuery;
    protected $tabStrip: JQuery;
    protected $tabContent: JQuery;
    protected tabs: Array<TabsStripTab2Type>;
    public context: TabStrip2ViewModel;

    public constructor(viewModel: any, protected $element?: JQuery<HTMLElement>) {
        super(viewModel, $element);
        this.tabs = [];
    }

    public async getElementAsync(): Promise<JQuery> {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.$element || $('<div>');
        this.$tabStrip = $('<ul class="c-list-wrapper">').appendTo(this.jQueryControl);
        this.$tabContent = $('<div class="c-tabs-wrapper">').appendTo(this.jQueryControl);

        this.applyBindings(this.jQueryControl, {
            controlName: 'tabstrip2',
            controlIdent: this.identifier,
            loader: this.context.isBusy,
            tooltip: this.context.tooltip,
            invalid: this.context.invalidationText,
            visible: this.context.isVisible
        });

        this.applyBindings(this.$tabStrip, {
            visible: this.context.showTabs,
            styles: this.definePureComputed({
                read: () => {
                    return !this.context.showTabs() ? ['hidden'] : [];
                }
            }),
            attr: {
                role: 'tablist'
            }
        });

        let oldIndex = -1;
        this.$tabStrip.sortable({
            axis: 'x',
            opacity: 0.6,
            revert: true,
            helper: 'clone',
            zIndex: 99999,
            cursor: 'grabbing',
            disabled: !this.context.isSortable(),
            start: (event, ui) => {
                // Platzhalter wird angepasst
                const item = ui.item;
                const classes = item.attr('class');
                ui.placeholder.addClass(classes)
                const height = item.height();
                const width = item.width();
                ui.placeholder.height(height).width(width);

                // Wir merken uns den alten Index
                oldIndex = item.index();
            },
            stop: (event, ui) => {
                const newIndex = ui.item.index();
                if (oldIndex === newIndex)
                    return;

                this.context.onReordering(oldIndex, newIndex);
            },
        });

        this.defineSubscription(this.context.isSortable, (value) => {
            this.$tabStrip.sortable("option", "disabled", !value);
        });

        this.defineSubscription(this.context.tabs, async () => await this.renderTabsAsync(this.context.getUISortedTabs()));
        await this.renderTabsAsync(this.context.getUISortedTabs());

        this.widget(null);

        return this.jQueryControl;
    }

    protected async renderTabsAsync(tabViewModels: TabStripTab2ViewModel[]) {
        this.$tabStrip.empty();
        this.$tabContent.empty();

        const createdItems = await this.createItemsAsync(tabViewModels, async vm => {
            const control = new this.TabStripTab2(vm);
            const tuple = await control.getTupleAsync();
            const $element = tuple.control;
            return {
                control,
                $element
            };
        });

        if (this.isDisposed || this.context.isDisposed)
            return;

        for (const { control, $element } of createdItems) {
            this.tabs.unshift(control)

            $element?.appendTo(this.$tabContent);
            $element?.children().filter('.c-title').appendTo(this.$tabStrip);
        }

        if (this.context && !this.context.isDisposed) {
            const selectedIndex = this.context.selectedIndex();
            const tabsLength = this.tabs.length;
            if (selectedIndex > tabsLength - 1) {
                this.context.selectedIndex(0);
            }
        }

        // TODO: invalides HTML (DIV in UL)
        $('<div class="clear">').appendTo(this.$tabStrip);
    }

    protected getDisposeBehavior(): DisposeBehavior {
        return "Asynchronous";
    }
}

ComponentManager.setControl('TabStrip2', TabStrip2, 'RADPresentation.HTMLClient.DefaultViewExtensions');